import { useMutation } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { getAccessToken } from "../services/authStore";

import {
  useApiMutation,
  useApiQuery,
  useLocalizedApiQuery,
} from "../services/react-query";

export const useGetAllTranslations = (params) =>
  useLocalizedApiQuery({
    queryOptions: { queryKey: ["translations", "ui", params] },
    fetchOptions: { url: "/translations/ui", method: "GET", params },
  });

export const useGetTranslationsByKey = (key) =>
  useLocalizedApiQuery({
    queryOptions: {
      queryKey: ["translations", "ui", "key", key],
      enabled: !!key,
    },
    fetchOptions: { url: `/translations/ui/keys/${key}`, method: "GET" },
  });

export const useCreateTranslations = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "useCreateTranslation" },
    fetchOptions: { url: "/translations/ui", method: "POST" },
    invalidateQueryKeys: [["translations"]],
    successMessage: {
      title: "Translation created successfully",
      color: "green",
    },
  });

export const useGetCurrentVersion = () =>
  useLocalizedApiQuery({
    queryOptions: { queryKey: ["translations", "version", "current"] },
    fetchOptions: { url: "/translations/ui/version", method: "GET" },
  });

export const useUpdateTranslation = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "useUpdateTranslation" },
    fetchOptions: { url: "/translations/ui", method: "PATCH" },
    invalidateQueryKeys: [["translations"]],
    successMessage: {
      title: "Translations updated successfully",
      color: "green",
    },
  });

export const useDeleteTranslation = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "useDeleteTranslation" },
    fetchOptions: { url: "/translations/ui", method: "DELETE" },
    invalidateQueryKeys: [["translations"]],
    successMessage: {
      title: "Translations deleted successfully",
      color: "green",
    },
  });

export const useGetVersionInfo = () =>
  useLocalizedApiQuery({
    queryOptions: { queryKey: ["translations", "version", "info"] },
    fetchOptions: { url: "/translations/ui/version/info", method: "GET" },
  });

export const usePublishVersion = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "usePublishVersion" },
    fetchOptions: { url: "/translations/ui/version/publish", method: "POST" },
    invalidateQueryKeys: [["translations"]],
    successMessage: {
      title: "Version published successfully",
      color: "green",
    },
  });

export function useExportTranslations() {
  return useMutation({
    mutationFn: async ({ format }) => {
      const token = await getAccessToken();

      const response = await fetch(
        `/api/translations/ui/export?format=${format}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to export translations");
      }

      const contentDisposition = response.headers.get("Content-Disposition");
      let fileName = `ui-translations-${new Date().toISOString().split("T")[0].replace(/-/g, "")}.${format}`;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }

      return response.blob().then((blob) => ({
        blob,
        fileName,
      }));
    },
    onSuccess: ({ blob, fileName }) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      notifications.show({
        title: "Success",
        message: "Translations exported successfully",
        color: "green",
      });
    },
    onError: (error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to export translations",
        color: "red",
      });
    },
  });
}

export const useImportTranslations = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "useImportTranslations" },
    fetchOptions: { url: "/translations/ui/import", method: "POST" },
    invalidateQueryKeys: [["translations"]],
    successMessage: {
      title: "Translations imported successfully",
      color: "green",
    },
  });

export const useGetTranslationCoverage = () =>
  useLocalizedApiQuery({
    queryOptions: { queryKey: ["translations", "coverage"] },
    fetchOptions: { url: "/translations/ui/coverage", method: "GET" },
  });

// AI notes per code key (description, where used, draft translations). They
// only change with a deploy, so they are fetched once, under a key outside
// ["translations"]: every translation edit invalidates that one.
export const useGetTranslationKeyMeta = () =>
  useApiQuery({
    queryOptions: { queryKey: ["translationKeyMeta"], staleTime: Infinity },
    fetchOptions: { url: "/translations/ui/meta", method: "GET" },
  });
